import { Icon } from '@buggy/shared'
import { Button, Dropdown, Tooltip, notification } from 'antd'
import { ItemType } from 'antd/es/menu/hooks/useItems'
import { compact, isEmpty, isUndefined } from 'lodash-es'
import { FunctionComponent } from 'react'
import { useCss } from 'react-use'
import { useTheme } from 'styled-components'
import { z } from 'zod/v4'
import { FeatureFlag } from '@/api/user-api'
import { useRemoveKolsFromDirectoryMutation } from '@/api/workspace-api/directory-api'
import { useDeleteKolsFromKolCollectionMutation } from '@/api/workspace-api/kol-collections-api'
import AddToCollectionButton from '@/components/collection/add-to-collection-button'
import { useAddKolsToPredictiveReportModal } from '@/components/common/modals/hooks/use-add-kols-to-predictive-report-modal'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import UnlockSnapshotTooltip from '@/components/kol/detail/unlock-snapshot-tooltip'
import useHandleClickUnlockButton from '@/components/kol/hooks/use-handle-click-unlock-button'
import { VipOnlyModal } from '@/components/modal/vip-only-modal'
import { useDispatch } from '@/hooks/use-dispatch'
import useFeatureFlags from '@/hooks/use-feature-flags'
import useIsQuotaBasedWorkspace from '@/hooks/use-is-quota-based-workspace'
import {
  useCampaignActionPermission,
  useCollectionActionPermission,
  useDirectoryActionPermission,
  usePredictiveReportActionPermission,
  useReportActionPermission,
  useUnlockKolDetailPermission,
} from '@/hooks/use-permission'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { setOpen as setAddKolsToCampaignModalOpen } from '@/store/add-kols-to-campaign-modal'
import { setOpen as setAddKolsToReportModalOpen } from '@/store/add-kols-to-report-modal'
import { setMetaData, setOpen } from '@/store/kol-cooperation-detail-drawer'
import { Optional } from '@/types/optional'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import type { AvailableUnlockData } from '@/types/schema/kol/unlock'
import { ampli, type VisitKolDrawerProperties } from '@/utils/ampli'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

export interface KolActionsDropdownProps {
  actions?: ActionType[]
  kolInfo: Pick<
    KolSchemaModel,
    | 'id'
    | 'name'
    | 'customizedTagsNames'
    | 'verified'
    | 'isAddedToDirectory'
    | 'potential'
    | 'isCollectionKol'
    | 'follower'
    | 'isRadarStar'
    | 'isUnlocked'
    | 'countryCode'
    | 'isExpired'
  > & { collection_kol_id?: string }
  availableUnlockData?: AvailableUnlockData[]
  isKolInfoLocked?: boolean
  from?: VisitKolDrawerProperties['at']
  isClickedFromDrawer?: boolean
  isDisabled?: boolean
  collectionId?: number
}

const actionTypeSchema = z.enum([
  'unlock',
  'viewCooperationDetail',
  'addKolToCollection',
  'addKolToCampaign',
  'addKolToReport',
  'addKolToPredictiveReport',
  'removeFromDirectory',
  'removeFromKolCollection',
])
export type ActionType = z.infer<typeof actionTypeSchema>

const KolActionsDropdown: FunctionComponent<KolActionsDropdownProps> = ({
  actions = [
    actionTypeSchema.enum.viewCooperationDetail,
    actionTypeSchema.enum.addKolToCampaign,
    actionTypeSchema.enum.addKolToReport,
  ],
  isKolInfoLocked,
  kolInfo,
  from,
  isClickedFromDrawer,
  isDisabled = false,
  collectionId,
  availableUnlockData,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const dispatch = useDispatch()
  const menuCss = useCss({
    '.ant-dropdown-menu-item-divider': {
      '&:last-child': {
        display: 'none',
      },
    },
  })
  const openAddKolsToPredictiveReportModal = useAddKolsToPredictiveReportModal()

  const directoryActionPermission = useDirectoryActionPermission()
  const unlockKolDetailPermission = useUnlockKolDetailPermission()
  const campaignActionPermission = useCampaignActionPermission()
  const reportActionPermission = useReportActionPermission()
  const kolCollectionActionPermission = useCollectionActionPermission()
  const predictiveReportActionPermission = usePredictiveReportActionPermission()
  const {
    withInternalPermission,
    withReportPermission,
    withPredictiveReportPermission,
  } = usePermissions()
  const { withFeature } = useFeatureFlags()
  const {
    handleClickUnlock,
    isVipOnlyModalOpen,
    setIsVipOnlyModalOpen,
    isUnlocking,
  } = useHandleClickUnlockButton(from, isClickedFromDrawer)
  const [removeKolsFromDirectory, { isLoading: isRemovingFromDirectory }] =
    useRemoveKolsFromDirectoryMutation()
  const [
    deleteKolsFromKolCollection,
    { isLoading: isDeletingKolsFromKolCollection },
  ] = useDeleteKolsFromKolCollectionMutation()
  const isQuotaBasedWorkspace = useIsQuotaBasedWorkspace()

  const divider: ItemType = { type: 'divider' }
  const unlockItems: ItemType[] =
    actions.includes('unlock') && isKolInfoLocked && isQuotaBasedWorkspace
      ? [
          {
            key: actionTypeSchema.enum.unlock,
            icon: <Icon color={theme.colors.plan.premium} name='lock' />,
            disabled: !unlockKolDetailPermission,
            label: (
              <UnlockSnapshotTooltip
                availableUnlockData={availableUnlockData}
                style='compact'
              >
                <span
                  style={{ marginLeft: 4, color: theme.colors.plan.premium }}
                >
                  {formatMessage({ id: 'general:unlock_act' })}
                </span>
              </UnlockSnapshotTooltip>
            ),
          },
          divider,
        ]
      : []
  const collectionItems: ItemType[] = actions.includes('addKolToCollection')
    ? [
        {
          key: actionTypeSchema.enum.addKolToCollection,
          label: (
            <AddToCollectionButton
              containerRef={null}
              customButton={formatMessage({
                id: 'irm:community_add_to_collection',
              })}
              from={from}
              isClickedFromDrawer={isClickedFromDrawer}
              isDisabled={false}
              kol={kolInfo}
              shouldStopPropagationOnClick={false}
            />
          ),
        },
      ]
    : []
  const predictiveReportItem = withPredictiveReportPermission
    ? {
        disabled: !predictiveReportActionPermission,
        key: actionTypeSchema.enum.addKolToPredictiveReport,
        label: (
          <PermissionInsufficientTooltip
            insufficient={!predictiveReportActionPermission}
          >
            {formatMessage({ id: 'predictive_report:add_kols_modal_title' })}
          </PermissionInsufficientTooltip>
        ),
      }
    : undefined
  const menuItems = ((): ItemType[] => {
    const viewCooperationDetailItems: ItemType[] = actions.includes(
      'viewCooperationDetail',
    )
      ? [
          {
            key: actionTypeSchema.enum.viewCooperationDetail,
            label: formatMessage({
              id: 'irm:community_view_relation_info',
            }),
          },
          divider,
        ]
      : []

    const campaignItem: Optional<ItemType> =
      actions.includes('addKolToCampaign') && withFeature(FeatureFlag.DualWheel)
        ? {
            disabled:
              !campaignActionPermission ||
              (!withInternalPermission && !kolInfo.verified),
            key: actionTypeSchema.enum.addKolToCampaign,
            label: (
              <Tooltip
                title={((): Optional<string> => {
                  if (!campaignActionPermission) {
                    return formatMessage({
                      id: 'workspace:insufficient_permission_tooltip',
                    })
                  }

                  if (!withInternalPermission && !kolInfo.verified) {
                    return formatMessage({ id: 'kol:unverified_kol_tip' })
                  }

                  return undefined
                })()}
              >
                {formatMessage({ id: 'kol:add_to_campaign_act' })}
              </Tooltip>
            ),
          }
        : undefined

    const reportItem: Optional<ItemType> =
      actions.includes('addKolToReport') && withReportPermission
        ? {
            disabled: !reportActionPermission,
            key: actionTypeSchema.enum.addKolToReport,
            label: (
              <Tooltip
                title={
                  !reportActionPermission
                    ? formatMessage({
                        id: 'workspace:insufficient_permission_tooltip',
                      })
                    : undefined
                }
              >
                {formatMessage({ id: 'irm:community_add_to_report' })}
              </Tooltip>
            ),
          }
        : undefined
    const removeFromDirectoryItem: Optional<ItemType> =
      actions.includes('removeFromDirectory') && kolInfo.isAddedToDirectory
        ? {
            disabled: !directoryActionPermission,
            key: actionTypeSchema.enum.removeFromDirectory,
            label: (
              <Tooltip
                title={((): Optional<string> => {
                  if (!directoryActionPermission) {
                    return formatMessage({
                      id: 'workspace:insufficient_permission_tooltip',
                    })
                  }

                  return undefined
                })()}
              >
                {formatMessage({
                  id: 'kol_asset_catalog:remove_from_catalog_act',
                })}
              </Tooltip>
            ),
          }
        : undefined

    const removeFromKolCollectionItem: Optional<ItemType> =
      actions.includes('removeFromKolCollection') &&
      kolInfo.isCollectionKol &&
      !isUndefined(collectionId) &&
      kolInfo.collection_kol_id
        ? {
            disabled: !kolCollectionActionPermission,
            key: actionTypeSchema.enum.removeFromKolCollection,
            label: (
              <Tooltip
                title={((): Optional<string> => {
                  if (!kolCollectionActionPermission) {
                    return formatMessage({
                      id: 'workspace:insufficient_permission_tooltip',
                    })
                  }

                  return undefined
                })()}
              >
                {formatMessage({
                  id: 'general:remove_from_kol_collection',
                })}
              </Tooltip>
            ),
          }
        : undefined

    const prependedItems = compact([
      ...unlockItems,
      ...viewCooperationDetailItems,
      ...collectionItems,
      predictiveReportItem,
      campaignItem,
      reportItem,
    ])

    return compact([
      ...prependedItems,
      isEmpty(prependedItems) ? undefined : divider,
      removeFromDirectoryItem,
      removeFromKolCollectionItem,
    ])
  })()

  const handleClick = async (key: ActionType): Promise<void> => {
    switch (key) {
      case 'viewCooperationDetail':
        dispatch(
          setMetaData({
            kolId: kolInfo.id,
            at: 'KOL Detail',
          }),
        )
        dispatch(setOpen(true))
        break
      case 'addKolToPredictiveReport':
        openAddKolsToPredictiveReportModal({
          candidateList: [
            {
              uuid: kolInfo.id,
              name: kolInfo.name,
              follower_count: kolInfo.follower,
              is_locked: !kolInfo.isUnlocked,
              is_verified: kolInfo.verified,
              is_radar_star: kolInfo.isRadarStar,
            },
          ],
          from,
          isClickedFromDrawer,
        })
        break
      case 'addKolToCampaign':
        dispatch(
          setAddKolsToCampaignModalOpen({
            open: true,
            isClickedFromDrawer,
            kols: [kolInfo],
            from,
          }),
        )
        break
      case 'addKolToReport':
        dispatch(
          setAddKolsToReportModalOpen({
            open: true,
            isClickedFromDrawer,
            kols: [kolInfo],
            from,
          }),
        )
        break
      case 'unlock':
        await handleClickUnlock(kolInfo)
        break
      case 'removeFromDirectory':
        try {
          await removeKolsFromDirectory([kolInfo.id]).unwrap()
          ampli.removeKolFromDirectory({
            kolIds: [kolInfo.id],
            at: from,
          })
        } catch (error) {
          logger.error(error)
          notification.error({
            message: formatMessage({
              id: 'general:operation_failure',
            }),
          })
        }
        break
      case 'removeFromKolCollection':
        if (
          isUndefined(collectionId) ||
          isUndefined(kolInfo.collection_kol_id) ||
          !kolInfo.isCollectionKol
        ) {
          return
        }

        try {
          await deleteKolsFromKolCollection({
            id: collectionId,
            collection_kol_ids: [kolInfo.collection_kol_id],
            kol_ids: [kolInfo.id],
          }).unwrap()
        } catch (error) {
          logger.error(error)
          notification.error({
            message: formatMessage({
              id: 'general:operation_failure',
            }),
          })
        }

        break
    }
  }

  if (isEmpty(menuItems)) {
    return null
  }

  return (
    <>
      <Dropdown
        disabled={isDisabled}
        menu={{
          className: menuCss,
          items: menuItems,
          onClick: toVoidFunction(async ({ key }): Promise<void> => {
            await handleClick(actionTypeSchema.parse(key))
          }),
        }}
        placement='bottomRight'
        trigger={['click']}
      >
        <Tooltip
          placement='right'
          title={
            isUnlocking
              ? undefined
              : formatMessage({
                  id: 'kol:quick_actions_tooltip_other_operation',
                })
          }
        >
          <Button
            icon={<Icon name='more-horizontal' />}
            loading={
              isUnlocking ||
              isRemovingFromDirectory ||
              isDeletingKolsFromKolCollection
            }
          />
        </Tooltip>
      </Dropdown>
      {!isEmpty(unlockItems) && (
        <VipOnlyModal
          canContinueBrowsingFromProp
          onlyQuota
          source='Unlock KD Data'
          title={formatMessage(
            {
              id: 'kol:description_kol_info_quota_not_enough',
            },
            { br: <br /> },
          )}
          visible={isVipOnlyModalOpen}
          onClose={(): void => {
            setIsVipOnlyModalOpen(false)
          }}
        />
      )}
    </>
  )
}

export default KolActionsDropdown
